import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Calendar, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatTripRange } from "../lib/dateLocal";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { formatUsd } from "../lib/currencyDisplay";
import { pageItems } from "../lib/pageItems";
import ReportIssueModal from "../components/ReportIssueModal";

export default function RenterDashboard() {
  const [bookings, setBookings] = useState([]);
  const [parkingBookings, setParkingBookings] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [reportBooking, setReportBooking] = useState(null);

  const load = async () => {
    setLoading(true);
    const sections = [
      { label: "bookings", set: setBookings, pick: (r) => pageItems(r.data) },
      { label: "wallet", set: setWallet, pick: (r) => r.data },
      { label: "parking bookings", set: setParkingBookings, pick: (r) => pageItems(r.data) },
      { label: "loyalty", set: setLoyalty, pick: (r) => r.data, fallback: null },
    ];
    const results = await Promise.allSettled([
      axios.get(`${API}/bookings/mine`, { withCredentials: true }),
      axios.get(`${API}/wallet/me`, { withCredentials: true }),
      axios.get(`${API}/parking-bookings/mine`, { withCredentials: true }),
      axios.get(`${API}/loyalty/me`, { withCredentials: true }),
    ]);
    const failed = [];
    results.forEach((r, i) => {
      const sec = sections[i];
      if (r.status === "fulfilled") sec.set(sec.pick(r.value));
      else {
        if ("fallback" in sec) sec.set(sec.fallback);
        failed.push(sec.label);
      }
    });
    setLoadErrors(failed);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upcoming = bookings.filter(b => ["confirmed", "active"].includes(b.status));
  const completed = bookings.filter(b => b.status === "completed");

  const cancelParking = async (id) => {
    if (!window.confirm("Cancel this parking booking?")) return;
    try {
      await axios.post(`${API}/parking-bookings/${id}/cancel`, {}, { withCredentials: true });
      toast.success("Parking cancelled"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="renter-dashboard">
      <div className="eyebrow mb-2">Renter</div>
      <h1 className="zc-headline text-4xl md:text-5xl">My trips</h1>

      {loading && (
        <p className="text-sm text-[var(--zc-text-3)] mt-4">Loading…</p>
      )}
      {loadErrors.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] text-sm flex flex-wrap items-center justify-between gap-2">
          <span className="text-[var(--zc-text-2)]">
            Some sections failed to load: {loadErrors.join(", ")}.
          </span>
          <button type="button" onClick={load} className="px-3 h-8 zc-btn-ghost text-xs font-semibold">
            Retry
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <div className="zc-card p-5">
          <div className="eyebrow">Upcoming</div>
          <div className="zc-stat text-3xl mt-2">{upcoming.length}</div>
        </div>
        <div className="zc-card p-5">
          <div className="eyebrow">Completed</div>
          <div className="zc-stat text-3xl mt-2">{completed.length}</div>
        </div>
        <div className="zc-card p-5">
          <div className="eyebrow">Z-Cars Tokens</div>
          <div className="zc-stat text-3xl mt-2">{loyalty?.loyalty?.zct_balance ?? 0}</div>
          <div className="text-xs text-[var(--zc-text-3)] mt-1">Earn {loyalty?.earn_rate ?? 5} ZCT per $1 spent</div>
        </div>
      </div>

      <div className="mt-10 zc-card p-6" data-testid="renter-bookings-summary">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Calendar size={16} className="text-[var(--zc-accent)]" />
              Car bookings
            </div>
            <p className="text-sm text-[var(--zc-text-2)] mt-1 max-w-xl">
              Manage trips, inspections, trip messages, reviews, and past vehicles in one place.
            </p>
          </div>
          <Link
            to="/profile/bookings"
            className="zc-btn-primary px-5 h-10 inline-flex items-center gap-2 text-sm shrink-0"
            data-testid="renter-go-bookings"
          >
            My bookings <ChevronRight size={16} />
          </Link>
        </div>
        {!loading && upcoming.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[var(--zc-border)] space-y-3">
            <div className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Next up</div>
            {upcoming.slice(0, 2).map(b => (
              <div
                key={b.booking_id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--zc-bg-3)] transition"
                data-testid={`renter-upcoming-${b.booking_id}`}
              >
                <Link
                  to={`/profile/bookings?booking=${b.booking_id}`}
                  className="flex items-center gap-3 min-w-0 flex-1"
                >
                  {b.vehicle_snapshot?.image && (
                    <img src={b.vehicle_snapshot.image} className="w-14 h-10 object-cover rounded shrink-0" alt="" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">
                      {b.vehicle_snapshot?.year} {b.vehicle_snapshot?.make} {b.vehicle_snapshot?.model}
                    </div>
                    <div className="text-xs text-[var(--zc-text-3)]">{formatTripRange(b.start_date, b.end_date)}</div>
                  </div>
                  <div className="text-xs flex items-center gap-1.5 shrink-0">
                    <span className={"dot " + bookingStatusDotClass(b.status)} />
                    {formatBookingStatus(b)}
                  </div>
                </Link>
                <button
                  type="button"
                  className="text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-text)] shrink-0"
                  onClick={() => setReportBooking(b)}
                  data-testid={`renter-report-issue-${b.booking_id}`}
                >
                  Report issue
                </button>
              </div>
            ))}
          </div>
        )}
        {!loading && bookings.length === 0 && (
          <div className="mt-5 pt-5 border-t border-[var(--zc-border)] text-sm text-[var(--zc-text-3)]">
            No trips yet — go find your dream drive.
          </div>
        )}
      </div>

      {/* Parking bookings */}
      <div className="mt-12">
        <div className="eyebrow mb-3">My parking bookings</div>
        <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table" data-testid="my-parking-bookings">
          <div className="hidden md:grid md:grid-cols-[1.5fr,1.2fr,0.8fr,1fr,1fr,1fr] zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
            <div>Spot</div><div>Window</div><div>Hours</div><div>Total</div><div>Status</div><div></div>
          </div>
          {parkingBookings.length === 0 && <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No parking bookings yet.</div>}
          {parkingBookings.map(b => (
            <div key={b.booking_id} className="zc-stack-row md:grid md:grid-cols-[1.5fr,1.2fr,0.8fr,1fr,1fr,1fr] md:items-center px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 text-sm">
              <div data-label="Spot" className="flex items-center gap-3 min-w-0">
                {b.spot_snapshot?.image && <img src={b.spot_snapshot.image} className="w-14 h-10 object-cover rounded shrink-0" alt="" />}
                <div className="min-w-0">
                  <div className="font-semibold line-clamp-1">{b.spot_snapshot?.name}</div>
                  <div className="text-xs text-[var(--zc-text-3)]">{b.spot_snapshot?.city}</div>
                </div>
              </div>
              <div data-label="Window" className="text-[var(--zc-text-2)] text-xs break-words">{formatTripRange(b.check_in, b.check_out) || "—"}</div>
              <div data-label="Hours">{b.hours}h</div>
              <div data-label="Total" className="font-semibold">${formatUsd(b.total)}</div>
              <div data-label="Status" className="text-[var(--zc-text)] flex items-center gap-2">
                <span className={"dot " + bookingStatusDotClass(b.status)} />
                {formatBookingStatus(b)}
              </div>
              <div data-label="Actions" className="zc-stack-actions flex justify-start md:justify-end">
                {(b.status === "confirmed" || b.status === "active") && (
                  <button className="text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-text)]" onClick={() => cancelParking(b.booking_id)} data-testid={`pcancel-${b.booking_id}`}>Cancel</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ReportIssueModal
        booking={reportBooking}
        open={!!reportBooking}
        onClose={() => setReportBooking(null)}
      />
    </div>
  );
}
